// WebMCP — expose a couple of READ-ONLY public discovery tools to in-browser AI
// agents via the experimental `navigator.modelContext` API.
//
// Safety/security:
// - Feature-detected: if `navigator.modelContext` is absent (every normal
//   browser today) this is a no-op, so it changes nothing for human users.
// - Read-only: the tools only return the same public venue data the consumer
//   feed already shows. No operator/admin/write actions are exposed.
// - Wrapped in try/catch so agent wiring can never break the app.
import { CITIES, isCityId } from "@/lib/cities";
import { listRestaurants } from "@/data/restaurants";
import { isRestaurantOpen } from "@/lib/openingHours";
import { foldText } from "@/lib/feedFilter";

type ToolResult = { content: Array<{ type: "text"; text: string }> };

interface WebMcpTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (args: Record<string, unknown>) => Promise<ToolResult>;
}

function asText(data: unknown): ToolResult {
  return { content: [{ type: "text", text: JSON.stringify(data) }] };
}

const tools: WebMcpTool[] = [
  {
    name: "resto_list_cities",
    description:
      "List the Romanian cities Resto covers. Returns each city's id (for resto_search_venues) and display name.",
    inputSchema: { type: "object", properties: {}, additionalProperties: false },
    async execute() {
      return asText(CITIES);
    },
  },
  {
    name: "resto_search_venues",
    description:
      "Search public restaurants in a Resto city by free text (name, neighborhood, cuisine, or tag). " +
      "Read-only public data: returns name, neighborhood, cuisine/tag slugs, and whether the venue is open right now.",
    inputSchema: {
      type: "object",
      properties: {
        city: { type: "string", description: "City id, e.g. 'cluj-napoca' (see resto_list_cities)." },
        query: { type: "string", description: "Optional free-text filter (diacritic-insensitive)." },
      },
      required: ["city"],
      additionalProperties: false,
    },
    async execute(args) {
      const city = String(args?.city ?? "");
      if (!isCityId(city)) return asText({ error: "Unknown city id", cities: CITIES });
      const now = new Date();
      const folded = foldText(String(args?.query ?? ""));
      const venues = (await listRestaurants(city, { visibleOnly: true }))
        .filter((r) => r.visible !== false)
        .map((r) => ({
          name: r.name,
          neighborhood: r.neighborhood ?? null,
          cuisine_slugs: r.cuisine_slugs ?? [],
          tag_slugs: r.tag_slugs ?? [],
          open: isRestaurantOpen(r, now),
        }))
        .filter((v) => {
          if (!folded) return true;
          const hay = foldText(
            [v.name, v.neighborhood, ...v.cuisine_slugs, ...v.tag_slugs].filter(Boolean).join(" "),
          );
          return hay.includes(folded);
        });
      return asText({ city, count: venues.length, venues });
    },
  },
];

export function initWebMcp(): void {
  try {
    const mc = (navigator as Navigator & { modelContext?: Record<string, unknown> }).modelContext;
    if (!mc) return;
    if (typeof mc.registerTool === "function") {
      for (const t of tools) (mc.registerTool as (tool: WebMcpTool) => void)(t);
    } else if (typeof mc.provideContext === "function") {
      (mc.provideContext as (ctx: { tools: WebMcpTool[] }) => void)({ tools });
    }
  } catch {
    /* never let agent wiring break the app for human users */
  }
}
